import React, { useState, useEffect, useCallback } from 'react';
import { GraduationCap, ClipboardList, Pencil, Trash2, Plus, CheckCircle, XCircle } from 'lucide-react';
import './PrestationsManagement.css';

const PrestationsManagement = ({ token, onBack }) => {
  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPrestation, setEditingPrestation] = useState(null);
  const [filterCategorie, setFilterCategorie] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    prix_ht: '',
    prix_mensuel_ht: '',
    type_facturation: 'ponctuel',
    categorie: 'Service',
    active: true,
    is_formation: false
  });

  const categories = ['Marketing', 'Développement', 'Service', 'Technique', 'Formation', 'Autre'];

  const fetchPrestations = useCallback(async () => {
    try {
      const response = await fetch(`/api/prestations/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPrestations(data);
    } catch (error) {
      console.error('Erreur chargement prestations:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPrestations();
  }, [fetchPrestations]);

  const openNewPrestation = () => {
    setFormData({
      nom: '',
      description: '',
      prix_ht: '',
      prix_mensuel_ht: '',
      type_facturation: 'ponctuel',
      categorie: 'Service',
      active: true,
      is_formation: false
    });
    setEditingPrestation(null);
    setShowModal(true);
  };

  const openEditPrestation = (prestation) => {
    setFormData({
      nom: prestation.nom,
      description: prestation.description || '',
      prix_ht: prestation.prix_ht || '',
      prix_mensuel_ht: prestation.prix_mensuel_ht || '',
      type_facturation: prestation.type_facturation || 'ponctuel',
      categorie: prestation.categorie || 'Service',
      active: prestation.active,
      is_formation: prestation.is_formation || false
    });
    setEditingPrestation(prestation);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nom || !formData.prix_ht) {
      alert('Nom et prix HT sont obligatoires');
      return;
    }

    try {
      const url = editingPrestation
        ? `/api/prestations/${editingPrestation.id}`
        : `/api/prestations`;

      const method = editingPrestation ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowModal(false);
        fetchPrestations();
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde prestation:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const toggleActive = async (prestation) => {
    try {
      await fetch(`/api/prestations/${prestation.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...prestation,
          active: !prestation.active
        })
      });
      fetchPrestations();
    } catch (error) {
      console.error('Erreur modification statut:', error);
    }
  };

  const deletePrestation = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette prestation ?')) return;

    try {
      const response = await fetch(`/api/prestations/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchPrestations();
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  const filteredPrestations = prestations.filter(p => {
    const matchCategorie = filterCategorie === 'all' || p.categorie === filterCategorie;
    const matchSearch = searchTerm === '' ||
      p.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCategorie && matchSearch;
  });

  const getCategorieColor = (categorie) => {
    const colors = {
      'Marketing': '#667eea',
      'Développement': '#28a745',
      'Service': '#17a2b8',
      'Technique': '#fd7e14',
      'Formation': '#6f42c1',
      'Autre': '#6c757d'
    };
    return colors[categorie] || colors['Autre'];
  };

  if (loading) {
    return (
      <div className="prestations-management">
        <div className="loading-spinner">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="prestations-management">
      <div className="prestations-header">
        <div className="header-left">
          <button className="btn-back" onClick={onBack}>
            ← Retour
          </button>
          <h1>Gestion des Prestations</h1>
        </div>
        <button className="btn-primary" onClick={openNewPrestation}>
          + Nouvelle Prestation
        </button>
      </div>

      {/* Statistiques */}
      <div className="prestations-stats">
        <div className="stat-card">
          <div className="stat-value">{prestations.length}</div>
          <div className="stat-label">Total prestations</div>
        </div>
        <div className="stat-card stat-active">
          <div className="stat-value">{prestations.filter(p => p.active).length}</div>
          <div className="stat-label">Actives</div>
        </div>
        <div className="stat-card stat-inactive">
          <div className="stat-value">{prestations.filter(p => !p.active).length}</div>
          <div className="stat-label">Inactives</div>
        </div>
        <div className="stat-card stat-categories">
          <div className="stat-value">{new Set(prestations.map(p => p.categorie)).size}</div>
          <div className="stat-label">Catégories</div>
        </div>
      </div>

      {/* Filtres */}
      <div className="prestations-filters">
        <input
          type="text"
          placeholder="Rechercher..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={filterCategorie}
          onChange={(e) => setFilterCategorie(e.target.value)}
          className="filter-select"
        >
          <option value="all">Toutes les catégories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Liste des prestations */}
      <div className="prestations-grid">
        {filteredPrestations.length === 0 ? (
          <div className="empty-state">
            <p>Aucune prestation trouvée</p>
            <button className="btn-primary" onClick={openNewPrestation}>Créer votre première prestation</button>
          </div>
        ) : (
          filteredPrestations.map(prestation => (
            <div
              key={prestation.id}
              className={`prestation-card ${!prestation.active ? 'inactive' : ''}`}
            >
              <div className="prestation-header">
                <span
                  className="categorie-badge"
                  style={{ backgroundColor: getCategorieColor(prestation.categorie) }}
                >
                  {prestation.categorie}
                </span>
                <div className="prestation-actions">
                  <button
                    className={`btn-toggle ${prestation.active ? 'active' : ''}`}
                    onClick={() => toggleActive(prestation)}
                    title={prestation.active ? 'Désactiver' : 'Activer'}
                  >
                    {prestation.active ? '✓' : '○'}
                  </button>
                </div>
              </div>
              <h3 className="prestation-nom">
                {prestation.is_formation && <span style={{ marginRight: '8px' }}><GraduationCap size={14} style={{display:'inline',verticalAlign:'middle'}} /></span>}
                {prestation.nom}
              </h3>
              {prestation.description && (
                <p className="prestation-description">{prestation.description}</p>
              )}
              <div className="prestation-prix-container">
                {parseFloat(prestation.prix_ht || 0) > 0 && (
                  <div className="prestation-prix">
                    {parseFloat(prestation.prix_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € HT
                  </div>
                )}
                {parseFloat(prestation.prix_mensuel_ht || 0) > 0 && (
                  <div className="prestation-prix-mensuel">
                    + {parseFloat(prestation.prix_mensuel_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €/mois
                  </div>
                )}
              </div>
              <div className="prestation-footer">
                <button className="btn-edit" onClick={() => openEditPrestation(prestation)}>
                  Modifier
                </button>
                <button className="btn-delete" onClick={() => deletePrestation(prestation.id)}>
                  Supprimer
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal création/édition */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingPrestation ? 'Modifier la prestation' : 'Nouvelle prestation'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nom de la prestation *</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    placeholder="Ex: Gestion campagnes Google Ads"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Description détaillée de la prestation..."
                    rows="3"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Prix ponctuel HT</label>
                    <div className="input-with-suffix">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.prix_ht}
                        onChange={(e) => setFormData({ ...formData, prix_ht: e.target.value })}
                        placeholder="0.00"
                      />
                      <span className="suffix">€ HT</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Abonnement mensuel HT</label>
                    <div className="input-with-suffix input-mensuel">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.prix_mensuel_ht}
                        onChange={(e) => setFormData({ ...formData, prix_mensuel_ht: e.target.value })}
                        placeholder="0.00"
                      />
                      <span className="suffix">€/mois</span>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Catégorie</label>
                    <select
                      value={formData.categorie}
                      onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Type de facturation</label>
                    <select
                      value={formData.type_facturation}
                      onChange={(e) => setFormData({ ...formData, type_facturation: e.target.value })}
                    >
                      <option value="ponctuel">Ponctuel uniquement</option>
                      <option value="mensuel">Mensuel uniquement</option>
                      <option value="les_deux">Les deux</option>
                    </select>
                  </div>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />
                    <span>Prestation active (visible lors de la création de devis)</span>
                  </label>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label" style={{ color: formData.is_formation ? '#6f42c1' : 'inherit' }}>
                    <input
                      type="checkbox"
                      checked={formData.is_formation}
                      onChange={(e) => setFormData({ ...formData, is_formation: e.target.checked, categorie: e.target.checked ? 'Formation' : formData.categorie })}
                    />
                    <span><GraduationCap size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Formation professionnelle (mentions légales obligatoires)</span>
                  </label>
                </div>

                {formData.is_formation && (
                  <div className="formation-info-box" style={{
                    backgroundColor: '#f8f4ff',
                    border: '2px solid #6f42c1',
                    borderRadius: '10px',
                    padding: '15px',
                    marginTop: '10px'
                  }}>
                    <h5 style={{ color: '#6f42c1', marginBottom: '10px' }}><ClipboardList size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Mentions obligatoires Formation</h5>
                    <p style={{ fontSize: '13px', color: '#555', marginBottom: '5px' }}>
                      Cette prestation nécessitera les informations suivantes lors de la création d'un devis/facture :
                    </p>
                    <ul style={{ fontSize: '12px', color: '#666', margin: '10px 0', paddingLeft: '20px' }}>
                      <li>Convention/Contrat de formation (numéro et date)</li>
                      <li>Intitulé de la formation</li>
                      <li>Bénéficiaire(s) de la formation</li>
                      <li>Modalité (Présentiel / Distanciel / Mixte)</li>
                      <li>Durée en heures</li>
                      <li>Dates de la formation</li>
                    </ul>
                    <p style={{ fontSize: '11px', color: '#6f42c1', fontWeight: '600' }}>
                      Mention légale : "Formation professionnelle au sens de l'article L.6313-1 du Code du travail"
                    </p>
                  </div>
                )}

                {(formData.prix_ht || formData.prix_mensuel_ht) && (
                  <div className="price-preview-container">
                    {formData.prix_ht && parseFloat(formData.prix_ht) > 0 && (
                      <div className="price-preview">
                        <h5>Prix ponctuel</h5>
                        <div className="preview-row">
                          <span>HT:</span>
                          <span>{parseFloat(formData.prix_ht || 0).toFixed(2)} €</span>
                        </div>
                        <div className="preview-row">
                          <span>TVA (20%):</span>
                          <span>{(parseFloat(formData.prix_ht || 0) * 0.20).toFixed(2)} €</span>
                        </div>
                        <div className="preview-row total">
                          <span>TTC:</span>
                          <span>{(parseFloat(formData.prix_ht || 0) * 1.20).toFixed(2)} €</span>
                        </div>
                      </div>
                    )}
                    {formData.prix_mensuel_ht && parseFloat(formData.prix_mensuel_ht) > 0 && (
                      <div className="price-preview price-preview-mensuel">
                        <h5>Abonnement mensuel</h5>
                        <div className="preview-row">
                          <span>HT/mois:</span>
                          <span>{parseFloat(formData.prix_mensuel_ht || 0).toFixed(2)} €</span>
                        </div>
                        <div className="preview-row">
                          <span>TVA (20%):</span>
                          <span>{(parseFloat(formData.prix_mensuel_ht || 0) * 0.20).toFixed(2)} €</span>
                        </div>
                        <div className="preview-row total">
                          <span>TTC/mois:</span>
                          <span>{(parseFloat(formData.prix_mensuel_ht || 0) * 1.20).toFixed(2)} €</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingPrestation ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrestationsManagement;
